class HeroSlider {
  constructor(element) {
    this.slider = element;
    this.container = element.querySelector('.js-slider-container');
    this.slides = element.querySelectorAll('.hero-slider__image');
    this.dots = element.querySelectorAll('.hero-slider__dot');
    this.currentSlide = 0;
    this.startX = 0;
    this.isDragging = false;
    this.dragThreshold = 5;
    this.autoplayEnabled = this.slider.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.slider.dataset.autoplaySpeed, 10) || 5000;
    this.autoplayTimer = null;
    this.isAnimating = false;

    // Autoplay stays locked until the non-active slides have been fetched. Rotating to a
    // slide whose image has not downloaded yet makes that image the Largest Contentful
    // Paint and pins LCP to however long it takes to arrive (measured: 16.5s on mobile).
    this.autoplayUnlocked = false;

    this.ensureInitialActive();
    this.initializeSlider();
    this.applyGradientFromActive();
    this.scheduleWarmup();
  }

  // Non-active slides ship as loading="lazy" fetchpriority="low" so they cannot compete
  // with the LCP image. Once the browser is idle (i.e. the first slide has painted) we
  // pull them in at low priority, then release the autoplay lock.
  scheduleWarmup() {
    if (!this.autoplayEnabled || this.slides.length < 2) {
      this.autoplayUnlocked = true;
      return;
    }

    const start = () => this.warmSlides().then(() => this.unlockAutoplay());

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(start, { timeout: 3000 });
    } else {
      setTimeout(start, 1500);
    }
  }

  warmSlides() {
    const pending = [];

    this.slides.forEach((slide, index) => {
      if (index === this.currentSlide) return;

      slide.querySelectorAll('img').forEach((img) => {
        img.loading = 'eager';
        if ('fetchPriority' in img) img.fetchPriority = 'low';
        if (img.complete) return;
        pending.push(
          new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          })
        );
      });
    });

    // Never block autoplay indefinitely on a slow or broken image.
    const cap = new Promise((resolve) => setTimeout(resolve, 8000));
    return Promise.race([Promise.all(pending), cap]);
  }

  unlockAutoplay() {
    if (this.autoplayUnlocked) return;
    this.autoplayUnlocked = true;
    if (this.autoplayEnabled) this.startAutoplay();
  }

  ensureInitialActive() {
    const slidesArr = Array.from(this.slides);
    let activeIndex = slidesArr.findIndex((s) => s.classList.contains('active'));
    if (activeIndex < 0) {
      activeIndex = 0;
      this.slides[0]?.classList.add('active');
      this.dots[0]?.classList.add('active');
    } else {
      slidesArr.forEach((s, i) => {
        if (i !== activeIndex) s.classList.remove('active');
      });
      this.dots.forEach((d, i) => {
        d.classList.toggle('active', i === activeIndex);
      });
    }
    this.currentSlide = activeIndex;
  }

  applyGradientFromActive() {
    const gradient = this.slides[this.currentSlide]?.dataset.gradient;
    if (gradient) this.slider.style.background = gradient;
  }

  initializeSlider() {
    this.container.addEventListener('touchstart', (e) => this.handleDragStart(e), { passive: true });
    this.container.addEventListener('touchmove', (e) => this.handleDragMove(e), { passive: false });
    this.container.addEventListener('touchend', () => this.handleDragEnd());
    this.container.addEventListener('mousedown', (e) => this.handleDragStart(e));
    this.container.addEventListener('mousemove', (e) => this.handleDragMove(e));
    this.container.addEventListener('mouseup', () => this.handleDragEnd());
    this.container.addEventListener('mouseleave', () => this.handleDragEnd());
    this.container.addEventListener(
      'click',
      (e) => {
        if (this.isDragging) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goToSlide(parseInt(dot.dataset.index, 10)));
    });

    if (this.autoplayEnabled) {
      // startAutoplay() is intentionally not called here — scheduleWarmup() releases it.
      this.container.addEventListener('mouseenter', () => this.stopAutoplay(), { passive: true });
      this.container.addEventListener('mouseleave', () => this.startAutoplay(), { passive: true });
    }
  }

  handleDragStart(e) {
    if (this.isAnimating) return;
    this.isDragging = false;
    this.startX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    this.stopAutoplay();
  }

  handleDragMove(e) {
    if (this.startX === 0 || this.isAnimating) return;
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const diff = this.startX - clientX;
    if (Math.abs(diff) > this.dragThreshold) {
      this.isDragging = true;
      e.preventDefault();
    }
    if (this.isDragging && Math.abs(diff) > 50) {
      if (diff > 0) this.goToNextSlide();
      else this.goToPrevSlide();
      this.handleDragEnd();
    }
  }

  handleDragEnd() {
    this.startX = 0;
    setTimeout(() => {
      this.isDragging = false;
    }, 100);
    if (this.autoplayEnabled) this.startAutoplay();
  }

  goToNextSlide() {
    this.goToSlide((this.currentSlide + 1) % this.slides.length);
  }

  goToPrevSlide() {
    this.goToSlide((this.currentSlide - 1 + this.slides.length) % this.slides.length);
  }

  goToSlide(index) {
    if (this.isAnimating || index === this.currentSlide) return;
    if (index < 0 || index >= this.slides.length) return;
    this.isAnimating = true;
    const prevIndex = this.currentSlide;
    const currentSlideEl = this.slides[prevIndex];
    const nextSlideEl = this.slides[index];

    // A manual swipe / dot click can beat the idle warmup, so promote the incoming slide.
    nextSlideEl.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
      if ('fetchPriority' in img) img.fetchPriority = 'high';
    });

    currentSlideEl.classList.remove('active');
    this.dots.forEach((dot) => dot.classList.remove('active'));

    requestAnimationFrame(() => {
      this.currentSlide = index;
      this.applyGradientFromActive();
      nextSlideEl.classList.add('active');
      this.dots.forEach((dot, i) => dot.classList.toggle('active', i === index));
      setTimeout(() => {
        this.isAnimating = false;
      }, 600);
    });
  }

  startAutoplay() {
    if (!this.autoplayEnabled || !this.autoplayUnlocked) return;
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      this.goToNextSlide();
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
}

function initGradientSliders() {
  document.querySelectorAll('.js-hero-slider').forEach((slider) => {
    if (slider.dataset.heroSliderBound === 'true') return;
    slider.dataset.heroSliderBound = 'true';
    new HeroSlider(slider);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGradientSliders);
} else {
  initGradientSliders();
}
